import React, {useMemo, useState} from "react";
import {css, cva} from "@panda/css";

import {PoiBusIcon, PoiSubwayIcon, PoiTrainIcon, PoiTramIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {useUniqPublicTransportRouteNames} from "../hooks/use_uniq_public_transport_route_names";
import {IPublicTransportRoute} from "../types/IPublicTransportRoute";

interface IProps {
    busRoutes?: IPublicTransportRoute[] | null;
    railwayRoutes?: IPublicTransportRoute[] | null;
    subwayRoutes?: IPublicTransportRoute[] | null;
    tramRoutes?: IPublicTransportRoute[] | null;
    activePoiRoute?: IPublicTransportRoute | null;
    onTransportRouteClick: (publicTransportRoute: IPublicTransportRoute) => void;
    onExpand?: () => void;
}

type TransportRouteGroup = "bus" | "railway" | "subway" | "tram";

const transportTypeIcons: Record<TransportRouteGroup, typeof PoiBusIcon> = {
    bus: PoiBusIcon,
    railway: PoiTrainIcon,
    subway: PoiSubwayIcon,
    tram: PoiTramIcon
};

const ADDITIONAL_ROUTES_FROM_INDEX = 10;

const getTitleForRoute = (route: IPublicTransportRoute) => {
    return `Od ${route.from} do ${route.to}`;
};

const isRouteActive = (route?: IPublicTransportRoute, activePoiRoute?: IPublicTransportRoute | null) => {
    return !!route && !!activePoiRoute && route?.id === activePoiRoute?.id;
};

export const RegionTransportRoutes = (props: IProps) => {
    const [showAdditionalRoutes, setShowAdditionalRoutes] = useState(false);

    const busRoutesRaw = useUniqPublicTransportRouteNames(props.busRoutes);
    const busRoutes = useMemo(() => {
        return busRoutesRaw?.sort((firstRoute, secondRoute) => {
            const isFirstRouteNightRoute = firstRoute.name[0] === "N";
            const isSecondRouteNightRoute = secondRoute.name[0] === "N";

            if ((isFirstRouteNightRoute && isSecondRouteNightRoute) || (!isFirstRouteNightRoute && !isSecondRouteNightRoute)) {
                return 0;
            }

            return isFirstRouteNightRoute ? 1 : -1;
        });
    }, [busRoutesRaw]);
    const subwayRoutes = useUniqPublicTransportRouteNames(props.subwayRoutes);
    const tramRoutes = useUniqPublicTransportRouteNames(props.tramRoutes);
    const railwayRoutes = useUniqPublicTransportRouteNames(props.railwayRoutes);

    const subwayRoutesFrom = subwayRoutes?.length || 0;
    const railwayRoutesLength = railwayRoutes?.length || 0;
    const tramRoutesLength = tramRoutes?.length || 0;
    const busRoutesLength = busRoutes?.length || 0;
    const totalRoutesLength = subwayRoutesFrom + railwayRoutesLength + tramRoutesLength + busRoutesLength;

    const routeIndexNormalization: Record<TransportRouteGroup, number> = {
        subway: 0,
        railway: subwayRoutesFrom,
        tram: subwayRoutesFrom + railwayRoutesLength,
        bus: subwayRoutesFrom + railwayRoutesLength + tramRoutesLength
    };

    const toggleShowAdditionalRoutes = () => {
        if (!showAdditionalRoutes) {
            props.onExpand?.();
        }

        setShowAdditionalRoutes((prevState) => !prevState);
    };

    const renderRoute = (type: TransportRouteGroup, route: IPublicTransportRoute, index: number) => {
        const active = isRouteActive(route, props.activePoiRoute);
        const isHidden = !showAdditionalRoutes && routeIndexNormalization[type] + index >= ADDITIONAL_ROUTES_FROM_INDEX;
        const Icon = transportTypeIcons[type];

        return (
            <li key={route.id} className={pillStyle({active, hidden: isHidden})} title={getTitleForRoute(route)}>
                <button type="button" className={routeButtonStyle} aria-pressed={active} onClick={() => props.onTransportRouteClick(route)}>
                    <Icon size="1.6" wrapperSize="2" wrapperColor="transparent" fill="var(--colors-gray-800)" className={routeIconStyle} />
                    <Text as="span" variant="info_txt_2" strong>
                        {route.name}
                    </Text>
                </button>
            </li>
        );
    };

    return (
        <div>
            <ul className={listWrapperStyle}>
                {subwayRoutes.map((route, index) => renderRoute("subway", route, index))}
                {railwayRoutes.map((route, index) => renderRoute("railway", route, index))}
                {tramRoutes.map((route, index) => renderRoute("tram", route, index))}
                {busRoutes.map((route, index) => renderRoute("bus", route, index))}
            </ul>

            {totalRoutesLength > ADDITIONAL_ROUTES_FROM_INDEX ? (
                <button type="button" className={toggleButtonResetStyle} aria-expanded={showAdditionalRoutes} onClick={toggleShowAdditionalRoutes}>
                    <Text as="div" variant="info_txt_1" className={toggleTextStyle}>
                        {showAdditionalRoutes ? "Pokaż mniej linii komunikacyjnych" : "Pokaż więcej linii komunikacyjnych"}
                    </Text>
                </button>
            ) : null}
        </div>
    );
};

const listWrapperStyle = css({
    listStyle: "none",
    display: "flex",
    flexWrap: "wrap",
    gap: 0.5,
    m: 0,
    mt: 0.5,
    p: 0
});

const pillStyle = cva({
    base: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "var(--colors-white)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "var(--colors-gray-800)",
        borderRadius: 1,
        height: "2.4rem",
        cursor: "pointer"
    },
    variants: {
        active: {
            true: {
                boxShadow: "0px 4px 4px 0px #00000040"
            },
            false: {}
        },
        hidden: {
            true: {
                display: "none"
            },
            false: {}
        }
    },
    defaultVariants: {
        active: false,
        hidden: false
    }
});

const routeIconStyle = css({
    pr: 1
});

const routeButtonStyle = css({
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "100%",
    background: "none",
    border: "none",
    py: 0,
    px: 1,
    cursor: "pointer"
});

const toggleTextStyle = css({
    mt: 1,
    textDecoration: "underline",
    cursor: "pointer"
});

const toggleButtonResetStyle = css({
    display: "block",
    width: "fit-content",
    background: "none",
    border: "none",
    p: 0,
    textAlign: "left",
    cursor: "pointer"
});
import React, {useMemo, useState} from "react";
import {css, cva} from "@panda/css";

import {PoiBusIcon, PoiSubwayIcon, PoiTrainIcon, PoiTramIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {useUniqPublicTransportRouteNames} from "../hooks/use_uniq_public_transport_route_names";
import {IPublicTransportRoute} from "../types/IPublicTransportRoute";

interface IProps {
    busRoutes?: IPublicTransportRoute[] | null;
    railwayRoutes?: IPublicTransportRoute[] | null;
    subwayRoutes?: IPublicTransportRoute[] | null;
    tramRoutes?: IPublicTransportRoute[] | null;
    activePoiRoute?: IPublicTransportRoute | null;
    onTransportRouteClick: (publicTransportRoute: IPublicTransportRoute) => void;
    onExpand?: () => void;
}

type TransportRouteGroup = "bus" | "railway" | "subway" | "tram";

const transportTypeIcons: Record<TransportRouteGroup, typeof PoiBusIcon> = {
    bus: PoiBusIcon,
    railway: PoiTrainIcon,
    subway: PoiSubwayIcon,
    tram: PoiTramIcon
};

const ADDITIONAL_ROUTES_FROM_INDEX = 10;

const getTitleForRoute = (route: IPublicTransportRoute) => {
    return `Od ${route.from} do ${route.to}`;
};

const isRouteActive = (route?: IPublicTransportRoute, activePoiRoute?: IPublicTransportRoute | null) => {
    return !!route && !!activePoiRoute && route?.id === activePoiRoute?.id;
};

export const RegionTransportRoutes = (props: IProps) => {
    const [showAdditionalRoutes, setShowAdditionalRoutes] = useState(false);

    const busRoutesRaw = useUniqPublicTransportRouteNames(props.busRoutes);
    const busRoutes = useMemo(() => {
        return busRoutesRaw?.sort((firstRoute, secondRoute) => {
            const isFirstRouteNightRoute = firstRoute.name[0] === "N";
            const isSecondRouteNightRoute = secondRoute.name[0] === "N";

            if ((isFirstRouteNightRoute && isSecondRouteNightRoute) || (!isFirstRouteNightRoute && !isSecondRouteNightRoute)) {
                return 0;
            }

            return isFirstRouteNightRoute ? 1 : -1;
        });
    }, [busRoutesRaw]);
    const subwayRoutes = useUniqPublicTransportRouteNames(props.subwayRoutes);
    const tramRoutes = useUniqPublicTransportRouteNames(props.tramRoutes);
    const railwayRoutes = useUniqPublicTransportRouteNames(props.railwayRoutes);

    const subwayRoutesFrom = subwayRoutes?.length || 0;
    const railwayRoutesLength = railwayRoutes?.length || 0;
    const tramRoutesLength = tramRoutes?.length || 0;
    const busRoutesLength = busRoutes?.length || 0;
    const totalRoutesLength = subwayRoutesFrom + railwayRoutesLength + tramRoutesLength + busRoutesLength;

    const routeIndexNormalization: Record<TransportRouteGroup, number> = {
        subway: 0,
        railway: subwayRoutesFrom,
        tram: subwayRoutesFrom + railwayRoutesLength,
        bus: subwayRoutesFrom + railwayRoutesLength + tramRoutesLength
    };

    const toggleShowAdditionalRoutes = () => {
        if (!showAdditionalRoutes) {
            props.onExpand?.();
        }

        setShowAdditionalRoutes((prevState) => !prevState);
    };

    const renderRoute = (type: TransportRouteGroup, route: IPublicTransportRoute, index: number) => {
        const active = isRouteActive(route, props.activePoiRoute);
        const isHidden = !showAdditionalRoutes && routeIndexNormalization[type] + index >= ADDITIONAL_ROUTES_FROM_INDEX;
        const Icon = transportTypeIcons[type];

        return (
            <li key={route.id} className={pillStyle({active, hidden: isHidden})} title={getTitleForRoute(route)}>
                <button type="button" className={routeButtonStyle} aria-pressed={active} onClick={() => props.onTransportRouteClick(route)}>
                    <Icon size="1.6" wrapperSize="2" wrapperColor="transparent" fill="var(--colors-gray-800)" className={routeIconStyle} />
                    <Text as="span" variant="info_txt_2" strong>
                        {route.name}
                    </Text>
                </button>
            </li>
        );
    };

    return (
        <div>
            <ul className={listWrapperStyle}>
                {subwayRoutes.map((route, index) => renderRoute("subway", route, index))}
                {railwayRoutes.map((route, index) => renderRoute("railway", route, index))}
                {tramRoutes.map((route, index) => renderRoute("tram", route, index))}
                {busRoutes.map((route, index) => renderRoute("bus", route, index))}
            </ul>

            {totalRoutesLength > ADDITIONAL_ROUTES_FROM_INDEX ? (
                <button type="button" className={toggleButtonResetStyle} aria-expanded={showAdditionalRoutes} onClick={toggleShowAdditionalRoutes}>
                    <Text as="div" variant="info_txt_1" className={toggleTextStyle}>
                        {showAdditionalRoutes ? "Pokaż mniej linii komunikacyjnych" : "Pokaż więcej linii komunikacyjnych"}
                    </Text>
                </button>
            ) : null}
        </div>
    );
};

const listWrapperStyle = css({
    listStyle: "none",
    display: "flex",
    flexWrap: "wrap",
    gap: 0.5,
    m: 0,
    mt: 0.5,
    p: 0
});

const pillStyle = cva({
    base: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "var(--colors-white)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: "var(--colors-gray-800)",
        borderRadius: 1,
        height: "2.4rem",
        cursor: "pointer"
    },
    variants: {
        active: {
            true: {
                boxShadow: "0px 4px 4px 0px #00000040"
            },
            false: {}
        },
        hidden: {
            true: {
                display: "none"
            },
            false: {}
        }
    },
    defaultVariants: {
        active: false,
        hidden: false
    }
});

const routeIconStyle = css({
    pr: 1
});

const routeButtonStyle = css({
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: "100%",
    background: "none",
    border: "none",
    py: 0,
    px: 1,
    cursor: "pointer"
});

const toggleTextStyle = css({
    mt: 1,
    textDecoration: "underline",
    cursor: "pointer"
});

const toggleButtonResetStyle = css({
    display: "block",
    width: "fit-content",
    background: "none",
    border: "none",
    p: 0,
    textAlign: "left",
    cursor: "pointer"
});
